import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CSSProperties, ReactNode } from 'react';

import Divider from '../divider/divider';
import { formatDateToClientFormat } from '../../../constants/helpers/dates';
import { PAYMENT_STATUSES } from '../../core/portal/client-balance/add-funds-to-your-wallet/interfaces';

import { DetailsType, OperationType } from './interface';

type DetailStatus = OperationType | PAYMENT_STATUSES;

interface Props {
  header: {
    icon: { component: any; color: CSSProperties['color'] };
    id: string;
    date: string;
    status: DetailStatus;
  };
  operationContent: ReactNode;
  detailsType?: DetailsType;
  error?: {
    errorCode?: string | null;
    errorMessage: string;
  };
}

const errorCodeFormatter = (errorCode?: string | null) => (errorCode ? `(code ${errorCode})` : '');

const FinishedMessage = ({ date, status }: { date: string; status: ReactNode }) => (
  <Typography>
    of <b>{date}</b> ended with the status of <b>{status}</b>
  </Typography>
);

const UnfinishedMessage = ({ date, status }: { date: string; status: ReactNode }) => (
  <Typography>
    of <b>{date}</b> is in the <b>{status}</b> status.
  </Typography>
);

const DetailsFactory = ({ header, operationContent, error, detailsType = 'operation' }: Props) => {
  const theme = useTheme();

  const getStatusText = (operationType: DetailStatus) => {
    switch (operationType) {
      case 'success': {
        return <span style={{ color: theme.palette.success.main }}>success</span>;
      }
      case 'failure': {
        return <span style={{ color: theme.palette.error.main }}>failure</span>;
      }
      case 'unknown': {
        return <span style={{ color: theme.palette.grey['700'] }}>unknown</span>;
      }
      case PAYMENT_STATUSES.CONFIRMED: {
        return <span style={{ color: theme.palette.success.main }}>confirmed</span>;
      }
      case PAYMENT_STATUSES.REJECTED: {
        return <span style={{ color: theme.palette.error.main }}>rejected</span>;
      }
      case PAYMENT_STATUSES.PENDING: {
        return <span style={{ color: theme.palette.warning.main }}>pending</span>;
      }
      default: {
        return <span style={{ color: theme.palette.common.black }}>undefined</span>;
      }
    }
  };

  return (
    <Box flexDirection="column" display="flex" justifyContent="space-between">
      <Box display="flex" flexDirection="row" alignItems="center" flex={1}>
        <header.icon.component
          style={{ fontSize: theme.elements.icon.size.xl, color: header.icon.color, marginRight: theme.spacing(2) }}
        />
        <Box>
          <Typography>
            The <b>{header.id}</b> {detailsType}
          </Typography>
          {header.status === PAYMENT_STATUSES.PENDING ? (
            <UnfinishedMessage
              status={getStatusText(header.status)}
              date={formatDateToClientFormat({ date: header.date })}
            />
          ) : (
            <FinishedMessage
              status={getStatusText(header.status)}
              date={formatDateToClientFormat({ date: header.date })}
            />
          )}
          {error ? (
            <Box mt={1}>
              <span style={{ color: theme.palette.error.main }}>
                Failure cause: <b>{`${error.errorMessage}`}</b> {errorCodeFormatter(error.errorCode)}
              </span>
            </Box>
          ) : null}
        </Box>
      </Box>
      <Divider />
      {operationContent}
    </Box>
  );
};

export default DetailsFactory;
